import { useState } from "react"
import Image from "next/image"
import { is } from "@react-three/fiber/dist/declarations/src/core/utils"
import { AnimatePresence, motion } from "framer-motion"

import Button from "../ui/Button"
import LazyVideo from "./LazyVideo"

interface ProjectItemProps {
  title: string
  description: string
  video: string
  testimonialImage: string
  testimonial: string
  skills: (string | null | undefined)[]
  linkText: string
  company: string
  isOriginal: boolean
  link: string
  uncollapsed?: boolean
  id: string
  vidIsWhite?: boolean
}

const ProjectItem = ({
  title,
  description,
  video,
  uncollapsed,
  testimonial,
  testimonialImage,
  skills,
  linkText,
  company,
  isOriginal,
  vidIsWhite,
  link,
  id,
}: ProjectItemProps) => {
  const [isOpen, setIsOpen] = useState(uncollapsed || false)

  const toggleOpen = () => setIsOpen(!isOpen)

  return (
    <div id={id}>
      <div className="relative aspect-video w-[90vw] lg:h-[281px] lg:w-[485px]">
        <div className="project-item-cutout relative h-full w-full overflow-hidden rounded-[15px]">
          <LazyVideo src={video} />
          {vidIsWhite && (
            <div className="project-item-cutout absolute top-0 z-10 h-full w-full overflow-hidden bg-black bg-opacity-[0.02]"></div>
          )}
        </div>
        <div className="flex justify-center sm:justify-start">
          <div>
            <button
              onClick={toggleOpen}
              title={"TODO:"}
              className="relative left-2 mt-4 flex w-full flex-col whitespace-nowrap leading-none text-jet transition-all hover:scale-[1.08] active:scale-100 lg:absolute lg:bottom-[115px] lg:left-[85%] lg:mt-0 lg:w-auto"
            >
              <h2 className="relative z-10 font-roboto text-[35px] lg:text-[40px]">
                {title}
              </h2>
              <h3 className="relative z-10 pl-3">
                {" "}
                <span className="font-var-h2 font-roboto text-[28px] lg:text-[30px]">
                  {company}
                </span>
              </h3>
              <div className="absolute -top-[-20%] left-1/2 h-[50%] w-[60%] -translate-x-1/2">
                <motion.div
                  className={`h-full w-full border-4  ${
                    !isOpen ? "border-signal" : "border-jet opacity-10"
                  }`}
                  initial={{ rotate: 0, scale: 1 }}
                  animate={{
                    rotate: [0, 10, -15, 0],
                    x: ["0%", `-10%`, "10%", "-10%", "0%"],
                    scale: [1, 1.2, 1],
                    transition: {
                      duration: 10,
                      ease: "linear",
                      repeat: Infinity,
                    },
                  }}
                ></motion.div>
              </div>
            </button>
          </div>
        </div>
      </div>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="pt-7 lg:pl-8 lg:pt-5 flex flex-col items-center sm:block text-center sm:text-left"
            initial={{ height: 0, scale: 0 }}
            animate={{ height: "auto", scale: 1 }}
            exit={{ height: 0, scale: 0 }}
            transition={{ duration: 0.1 }}
          >
            <ul className="flex flex-wrap  gap-2 gap-y-2 lg:gap-4">
              {skills.map((skill) => (
                <li
                  key={skill}
                  className="rounded-[10px] border-2 border-jet px-4 py-1.5 text-[14px]"
                >
                  {skill}
                </li>
              ))}
            </ul>
            <div className=" mt-6 flex flex-col lg:mt-4 lg:flex-row lg:items-center lg:gap-10 items-center">
              <p className="max-w-[400px] text-[16px] leading-tight">
                {description}
              </p>
              {link && <Button link={link} isExternal buttonText={linkText} />}
            </div>
            {testimonialImage && (
              <div className="mb-14 mt-6 flex items-center gap-5 ">
                <div>
                  <Image
                    src={testimonialImage}
                    width={64}
                    height={64}
                    className="rounded-full"
                    alt="testimonial image"
                  />
                </div>
                <p className="max-w-[200px] text-[14px] leading-tight">
                  {testimonial}
                </p>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default ProjectItem
